import styled from 'styled-components';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Design } from 'ui/icons';
import { LeftTooltip } from 'ui/tooltip';
import {
  InAppContentDefinition,
  InAppDestination,
  MessageMedium,
  MESSAGE_FORMAT,
} from 'modules/graphql/types';
import { MessagingPermissionOptions, TemplateEditViews } from './types';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { displayContent } from './content';
import { ButtonContentWrapper } from './DetailsEditSaveButton';
import { PreviewHeaderContainer, PreviewHeaderWrapper } from './EditorContentContainer.styled';
import { useErrorValidationContext } from '../context/ErrorValidationContext';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useGetCanManageBaseTemplates, useGetMessagingPermission } from './utils';

const StyledButton = styled(Button)<{ $hasError: boolean }>`
  &&& {
    border: ${({ theme, $hasError }) =>
      $hasError && `2px solid ${theme.colors.utilityColors.negative100}`};
  }
`;

type PreviewHeaderContentProps = {
  hasDesignError: boolean;
  isInAppWithRawDataOnly: boolean;
  isOnlyEditorChannel: boolean;
  isRokuOnlyDestination: boolean;
  setView: (templateEditView: TemplateEditViews) => void;
};

const PreviewHeaderContent = ({
  hasDesignError,
  isInAppWithRawDataOnly,
  isOnlyEditorChannel,
  isRokuOnlyDestination,
  setView,
}: PreviewHeaderContentProps) => {
  const hasContentAndDesignError = hasDesignError && !isInAppWithRawDataOnly;
  const { campaignId: isCampaignFlow, experimentFlow: isExperimentFlow } = useQueryParamContext();
  const messagingPermission = useGetMessagingPermission();
  const canManageBaseTemplates = useGetCanManageBaseTemplates();

  const isMissingManagePermissions =
    isCampaignFlow || isExperimentFlow
      ? messagingPermission === MessagingPermissionOptions.VIEW_ONLY
      : !canManageBaseTemplates;

  const tooltipContent = isMissingManagePermissions
    ? displayContent.tooltipManageBaseTemplatesPermission
    : displayContent.errDesign;

  const isEditDesignButtonDisabled =
    isInAppWithRawDataOnly || isMissingManagePermissions || isRokuOnlyDestination;

  return (
    <PreviewHeaderContainer>
      <span>{displayContent.designHeader}</span>
      {isOnlyEditorChannel && (
        <LeftTooltip
          isEnabled={hasContentAndDesignError || isMissingManagePermissions}
          tooltipContent={tooltipContent}
        >
          <StyledButton
            isDisabled={isEditDesignButtonDisabled}
            onClick={() => setView(TemplateEditViews.content)}
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.SECONDARY}
            $hasError={hasContentAndDesignError && !isEditDesignButtonDisabled}
          >
            <ButtonContentWrapper>
              <Design />
              {displayContent.editDesign}
            </ButtonContentWrapper>
          </StyledButton>
        </LeftTooltip>
      )}
    </PreviewHeaderContainer>
  );
};

export const PREVIEW_HEADER_WRAPPER = 'preview-header-wrapper';

export const PreviewHeader = () => {
  const { medium } = useTemplateQueryContext();
  const { isJourneyAssociated, setView, view } = useQueryParamContext();
  const { designContentErrors, designContentWarnings } = useErrorValidationContext();
  const { liveTemplate } = useEditableFieldsContext();

  const { mediumData = {} } = liveTemplate || {};
  const { messageFormat, destinations } = mediumData as InAppContentDefinition;

  const isRokuOnlyDestination =
    destinations?.filter((destination) => destination === InAppDestination.Roku) &&
    destinations?.length === 1;

  const isOnlyEditorChannel = medium === MessageMedium.Email || medium === MessageMedium.InApp;
  const isPreviewWithData = view === TemplateEditViews.preview;

  const hasDesignError = !!designContentErrors.length || !!designContentWarnings.length;

  const isInAppWithRawDataOnly =
    medium === MessageMedium.InApp && messageFormat === MESSAGE_FORMAT.Json;

  return (
    <>
      {!isJourneyAssociated && (
        <PreviewHeaderWrapper data-test={PREVIEW_HEADER_WRAPPER}>
          {!isPreviewWithData && (
            <PreviewHeaderContent
              hasDesignError={hasDesignError}
              isInAppWithRawDataOnly={isInAppWithRawDataOnly}
              isOnlyEditorChannel={isOnlyEditorChannel}
              isRokuOnlyDestination={isRokuOnlyDestination}
              setView={setView}
            />
          )}
        </PreviewHeaderWrapper>
      )}
    </>
  );
};
